// services/AdminUserApi.js
import axios from 'axios';

// Use the same environment variable as other services in the project
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost';

// Create axios instance for admin user API
const adminApiClient = axios.create({
  baseURL: apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
adminApiClient.interceptors.request.use(
  (config) => {
    // CRITICAL FIX: For MSIL users, use msil_id_token (contains email claims)
    // For MASS users, use sessionStorage token
    // Priority: msil_id_token > token (for MSIL users with Cognito tokens)
    const token = localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
adminApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      console.error('Unauthorized: Token expired or invalid - redirecting to login');
      
      // Clear all authentication data
      localStorage.removeItem('msil_id_token');
      localStorage.removeItem('msil_access_token');
      localStorage.removeItem('msil_code_used');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('role');
      sessionStorage.removeItem('email');
      sessionStorage.removeItem('uid');
      sessionStorage.removeItem('otpVerified');
      sessionStorage.removeItem('msil_code_used');
      
      // Redirect to login page
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      // Not authorized (not SUPER_ADMIN)
      console.error('Forbidden: User does not have SUPER_ADMIN role');
    }
    return Promise.reject(error);
  }
);

/**
 * Get all admin users (SUPER_ADMIN only)
 * @returns {Promise<Array>} List of admin users
 */
export const getAllAdminUsers = async () => {
  try {
    const response = await adminApiClient.get('/api/v1/admin/users');
    return response.data;
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }
};

/**
 * Get admin users by role
 * @param {string} role - "ADMIN" or "SUPER_ADMIN"
 * @returns {Promise<Array>} Filtered list of admin users
 */
export const getAdminUsersByRole = async (role) => {
  try {
    const response = await adminApiClient.get(`/api/v1/admin/users/role/${role}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching ${role} users:`, error);
    throw error;
  }
};

/**
 * Get specific admin user by email
 * @param {string} email - User's email address
 * @returns {Promise<Object>} Admin user details
 */
export const getAdminUser = async (email) => {
  try {
    const response = await adminApiClient.get(`/api/v1/admin/users/${email}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching user ${email}:`, error);
    throw error;
  }
};

/**
 * Create new admin user
 * @param {Object} userData - User data
 * @param {string} userData.email - User's email
 * @param {string} userData.role - "ADMIN" or "SUPER_ADMIN"
 * @param {string} userData.status - "ACTIVE" or "INACTIVE" (optional, defaults to "ACTIVE")
 * @param {string} userData.notes - Notes about the user (optional)
 * @returns {Promise<Object>} Creation response
 */
export const createAdminUser = async (userData) => {
  try {
    const response = await adminApiClient.post('/api/v1/admin/users', {
      email: userData.email,
      role: userData.role,
      status: userData.status || 'ACTIVE',
      notes: userData.notes || ''
    });
    return response.data;
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
};

/**
 * Update existing admin user
 * @param {string} email - User's email (path parameter)
 * @param {Object} userData - Updated user data
 * @param {string} userData.role - "ADMIN" or "SUPER_ADMIN"
 * @param {string} userData.status - "ACTIVE" or "INACTIVE"
 * @param {string} userData.notes - Notes about the user
 * @returns {Promise<Object>} Update response
 */
export const updateAdminUser = async (email, userData) => {
  try {
    const response = await adminApiClient.put(`/api/v1/admin/users/${email}`, userData);
    return response.data;
  } catch (error) {
    console.error(`Error updating user ${email}:`, error);
    throw error;
  }
};

/**
 * Delete admin user
 * @param {string} email - User's email
 * @returns {Promise<Object>} Deletion response
 */
export const deleteAdminUser = async (email) => {
  try {
    // VAPT FIX: URL-encode the email to handle special characters and XSS payloads
    // This allows deletion of malicious entries created before XSS validation
    const encodedEmail = encodeURIComponent(email);
    const response = await adminApiClient.delete(`/api/v1/admin/users/${encodedEmail}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting user ${email}:`, error);
    throw error;
  }
};

/**
 * Cleanup malicious admin users (users with XSS payloads in email)
 * This endpoint finds and deletes all users with XSS patterns in their email
 * that were created before XSS validation was implemented.
 * @returns {Promise<Object>} Cleanup response with count of deleted users
 */
export const cleanupMaliciousUsers = async () => {
  try {
    const response = await adminApiClient.post('/api/v1/admin/users/cleanup/malicious');
    return response.data;
  } catch (error) {
    console.error('Error cleaning up malicious users:', error);
    throw error;
  }
};

/**
 * Promote user from ADMIN to SUPER_ADMIN
 */
export const promoteToSuperAdmin = async (email) => {
  const user = await getAdminUser(email);
  return updateAdminUser(email, {
    role: 'SUPER_ADMIN',
    status: user.status,
    notes: user.notes + ' | Promoted to SUPER_ADMIN'
  });
};

/**
 * Demote user from SUPER_ADMIN to ADMIN
 */
export const demoteToAdmin = async (email) => {
  const user = await getAdminUser(email);
  return updateAdminUser(email, {
    role: 'ADMIN',
    status: user.status,
    notes: user.notes + ' | Demoted to ADMIN'
  });
};

/**
 * Toggle user status (ACTIVE ↔ INACTIVE)
 */
export const toggleUserStatus = async (email) => {
  const user = await getAdminUser(email);
  const newStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
  return updateAdminUser(email, {
    role: user.role,
    status: newStatus,
    notes: user.notes + ` | Status changed to ${newStatus}`
  });
};

/**
 * Check if current user is SUPER_ADMIN
 * @returns {boolean}
 */
export const isSuperAdmin = () => {
  const role = sessionStorage.getItem('role');
  return role === 'SUPER_ADMIN';
};

/**
 * Get current user email
 * @returns {string|null}
 */
export const getCurrentUserEmail = () => {
  return sessionStorage.getItem('email');
};

export default {
  getAllAdminUsers,
  getAdminUsersByRole,
  getAdminUser,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  cleanupMaliciousUsers,
  promoteToSuperAdmin,
  demoteToAdmin,
  toggleUserStatus,
  isSuperAdmin,
  getCurrentUserEmail
};

